import { useMemo, useState } from 'react';
import { IconSearch } from '@/components/Icons';
import { formatBRL } from '@/lib/format';
import { allocationAmount, FULL_BP, formatBp, inputToBp } from '@/lib/leverage';
import type { LeverageLead } from '@/lib/types';

export interface ClientRow {
  lead: LeverageLead;
  /** Capital de agora, em centavos. */
  capital: number;
  /** Quanto do capital ainda está livre, fora desta operação (bp). */
  freeBp: number;
  /** Fatia já executada nesta operação: fica como está. */
  executed: { percentBp: number; amount: number } | null;
  /** Fatia já salva nesta operação: com o mesmo %, o valor congelado continua valendo. */
  saved: { percentBp: number; amount: number } | null;
}

export interface Pick {
  percent: string;
  /** Ajustado à mão: as faixas não mexem mais nele. */
  manual: boolean;
}

interface ClientAllocationListProps {
  rows: ClientRow[];
  picks: Record<string, Pick>;
  onToggle: (row: ClientRow) => void;
  onPercent: (leadId: string, percent: string) => void;
}

/** O valor da fatia: o congelado, se o % não mudou; senão, capital de agora × %. */
export function shareAmount(row: ClientRow, bp: number): number {
  if (row.executed) return row.executed.amount;
  if (row.saved && row.saved.percentBp === bp) return row.saved.amount;
  return allocationAmount(row.capital, bp);
}

const normalize = (value: string) => value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * Os clientes com capital, para escolher quem entra e com quanto. Cada linha
 * mostra o capital, o que ainda está livre (as operações abertas somam no
 * máximo 100%) e o valor que vai para a corretora.
 */
export function ClientAllocationList({ rows, picks, onToggle, onPercent }: ClientAllocationListProps) {
  const [query, setQuery] = useState('');
  const [onlyPicked, setOnlyPicked] = useState(false);

  const visible = useMemo(() => {
    const term = normalize(query.trim());
    return rows.filter(
      (row) => (!onlyPicked || picks[row.lead.id]) && (!term || normalize(`${row.lead.name} ${row.lead.ownerName ?? ''}`).includes(term)),
    );
  }, [rows, picks, query, onlyPicked]);

  const pickedCount = Object.keys(picks).length;

  return (
    <section className="lev-block" aria-labelledby="lev-clientes">
      <div className="lev-block-head">
        <span id="lev-clientes" className="lev-block-title">Clientes</span>
        <span className="muted lev-block-hint">{pickedCount} de {rows.length} escolhidos</span>
      </div>

      <div className="lev-client-tools">
        <div className="search-input" style={{ flex: 1, minWidth: 180 }}>
          <span className="search-input-icon" aria-hidden><IconSearch /></span>
          <input
            className="inp"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Filtrar por cliente ou responsável"
            aria-label="Filtrar clientes"
          />
        </div>
        <label className="lev-check-label">
          <input type="checkbox" checked={onlyPicked} onChange={(event) => setOnlyPicked(event.target.checked)} />
          Só os escolhidos
        </label>
      </div>

      {rows.length === 0 ? (
        <p className="muted lev-empty-line">Nenhum cliente com aporte ainda. Os membros registram os aportes nesta mesma página.</p>
      ) : (
        <ul className="lev-clients" aria-label="Clientes com capital">
          {visible.map((row) => {
            const pick = picks[row.lead.id];
            const bp = row.executed ? row.executed.percentBp : pick ? inputToBp(pick.percent) : 0;
            const amount = shareAmount(row, bp);
            const over = Boolean(pick) && bp > row.freeBp;
            const missing = Boolean(pick) && !row.executed && bp <= 0;
            return (
              <li key={row.lead.id} className="lev-client" data-picked={pick ? 'true' : undefined} data-problem={over || missing ? 'true' : undefined}>
                <label className="lev-client-who">
                  <input
                    type="checkbox"
                    checked={Boolean(pick)}
                    disabled={Boolean(row.executed)}
                    onChange={() => onToggle(row)}
                    aria-describedby={`lev-cap-${row.lead.id}`}
                  />
                  <span style={{ minWidth: 0 }}>
                    <span className="lev-client-name truncate">{row.lead.name}</span>
                    <span className="lev-client-sub muted truncate">{row.lead.ownerName ?? 'Sem responsável'}</span>
                  </span>
                </label>
                <div className="lev-client-fig" id={`lev-cap-${row.lead.id}`}>
                  <span className="label">Capital</span>
                  <span className="num">{formatBRL(row.capital)}</span>
                </div>
                <div className="lev-client-fig">
                  <span className="label">Livre</span>
                  <span className={`num${row.freeBp <= 0 ? ' lev-text-danger' : ''}`}>{formatBp(Math.max(0, row.freeBp))}</span>
                </div>
                <div className="lev-client-pct">
                  {row.executed ? (
                    <span className="chip chip-positive" title="Já executado: para mudar, desmarque o visto na lista da operação">Executado · {formatBp(row.executed.percentBp)}</span>
                  ) : pick ? (
                    <div className="lev-pct">
                      <input
                        className="inp num"
                        inputMode="decimal"
                        value={pick.percent}
                        onChange={(event) => onPercent(row.lead.id, event.target.value.replace(/[^\d.,]/g, ''))}
                        placeholder="0"
                        aria-label={`% do capital de ${row.lead.name}`}
                        aria-invalid={over || missing}
                      />
                      <span className="lev-pct-sign" aria-hidden>%</span>
                    </div>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </div>
                <div className="lev-client-fig lev-client-amount">
                  <span className="label">Valor</span>
                  <span className="num">{pick || row.executed ? formatBRL(amount) : '—'}</span>
                </div>
                {over ? (
                  <span className="lev-client-warn" role="note">
                    {bp > FULL_BP
                      ? 'O % vai até 100.'
                      : `Passa de 100% do capital: só ${formatBp(Math.max(0, row.freeBp))} está livre nas operações abertas.`}
                  </span>
                ) : missing ? (
                  <span className="lev-client-warn" role="note">Informe o % — nenhuma faixa alcança este capital.</span>
                ) : null}
              </li>
            );
          })}
          {visible.length === 0 ? <li className="muted lev-empty-line">Nenhum cliente com esse filtro.</li> : null}
        </ul>
      )}
    </section>
  );
}
